import { ImageFormat, jpegBackupSource, Source } from '@westwing/ui-kit/Picture';
import { getMediaStringValue } from '@westwing/ui-kit/breakPoints';
import { IDSContentfulImageFile } from '@ids/types/contentful';

enum ImageWidths {
    CONTENTFUL_MOBILE_TABLET_LOW_DENSITY = 768,
    CONTENTFUL_MOBILE_TABLET_HIGH_DENSITY = 1440,
    CONTENTFUL_DESKTOP = 1920,
}

const contentfulWidthFormatter = (src: string, width: number, quality = 80) => ({
    webpSrc: `${src}?w=${width}&fm=webp&q=${quality}`,
    jpegSrc: `${src}?w=${width}&fm=jpg&q=${quality}`,
});

export const makeMediaSources = (
    quality: number,
    payload?: IDSContentfulImageFile | [IDSContentfulImageFile, IDSContentfulImageFile] | string
): Source[] => {
    let src;
    let mobileSource;

    if (Array.isArray(payload)) {
        const lastContentfulImageFile = payload[payload.length - 1];
        mobileSource = (payload[0] ?? lastContentfulImageFile)?.file.url;
        src = (payload[1] ?? lastContentfulImageFile)?.file.url;
    } else if (typeof payload === 'string') {
        src = payload;
    } else if (typeof payload?.file.url === 'string') {
        src = payload.file.url;
    }

    if (!src) {
        return [];
    }

    const { webpSrc: mobileTabletSrcWebp, jpegSrc: mobileTabletSrcJpeg } = contentfulWidthFormatter(
        mobileSource || src,
        ImageWidths.CONTENTFUL_MOBILE_TABLET_LOW_DENSITY,
        quality
    );
    const { webpSrc: mobileTabletSrcWebp2xDpi, jpegSrc: mobileTabletSrcJpeg2xDpi } = contentfulWidthFormatter(
        mobileSource || src,
        ImageWidths.CONTENTFUL_MOBILE_TABLET_HIGH_DENSITY,
        quality
    );

    const { webpSrc: desktopWebpDpiSrc, jpegSrc: desktopJpegDpiSrc } = contentfulWidthFormatter(
        src,
        ImageWidths.CONTENTFUL_DESKTOP,
        quality
    );

    return [
        ...jpegBackupSource(
            {
                media: getMediaStringValue('breakpointMobileSmall', true),
                url: mobileTabletSrcWebp,
                value: 1,
                format: ImageFormat.WEBP,
            },
            mobileTabletSrcJpeg
        ),

        ...jpegBackupSource(
            {
                media: getMediaStringValue('breakpointMobileSmall', true),
                url: mobileTabletSrcWebp,
                value: 2,
                format: ImageFormat.WEBP,
            },
            mobileTabletSrcJpeg
        ),

        ...jpegBackupSource(
            {
                media: getMediaStringValue('breakpointMobileSmall', undefined, 'breakpointTablet'),
                url: mobileTabletSrcWebp,
                value: 1,
                format: ImageFormat.WEBP,
            },
            mobileTabletSrcJpeg
        ),

        ...jpegBackupSource(
            {
                media: getMediaStringValue('breakpointMobileSmall', undefined, 'breakpointTablet'),
                url: mobileTabletSrcWebp2xDpi,
                value: 2,
                format: ImageFormat.WEBP,
            },
            mobileTabletSrcJpeg2xDpi
        ),

        ...jpegBackupSource(
            {
                media: getMediaStringValue('breakpointTablet', undefined, 'breakpointDesktop'),
                url: mobileTabletSrcWebp,
                value: 1,
                format: ImageFormat.WEBP,
            },
            mobileTabletSrcJpeg
        ),

        ...jpegBackupSource(
            {
                media: getMediaStringValue('breakpointTablet', undefined, 'breakpointDesktop'),
                url: mobileTabletSrcWebp2xDpi,
                value: 2,
                format: ImageFormat.WEBP,
            },
            mobileTabletSrcJpeg2xDpi
        ),

        ...jpegBackupSource(
            {
                media: getMediaStringValue('breakpointDesktop'),
                url: desktopWebpDpiSrc,
                value: 1,
                format: ImageFormat.WEBP,
            },
            desktopJpegDpiSrc
        ),

        ...jpegBackupSource(
            {
                media: getMediaStringValue('breakpointDesktop'),
                url: desktopWebpDpiSrc,
                value: 2,
                format: ImageFormat.WEBP,
            },
            desktopJpegDpiSrc
        ),
    ];
};
